<template>
  <div
    ref="$el"
    :class="cmpClass"
    @click="e => handleClick(e)"
    @mousedown="e => handleMousedown(e)"
    @mouseenter="e => handleMouseenter(e)"
    @mouseleave="e => handleMouseleave(e)"
    @mouseup="e => handleMouseup(e)"
  >
    <span :class="switchClass">
      <input
        ref="checkbox"
        type="checkbox"
        :name="name"
        :value="value"
        :checked="checked"
        :disabled="disabled"
        @focus="e => handleFocus(e)"
        @blur="e => handleBlur(e)"
      >
      <span
        :class="[
          'd-switch-indicator',
          '--color-bg-white',
        ]"
      >
        <Spinner v-if="loading" />
      </span>
    </span>
  </div>
</template>
<script setup lang="ts">
  import {
    ref, reactive, computed, watch, watchEffect, onMounted, onBeforeUnmount, withDefaults, inject,
  } from 'vue'
  import { Spinner } from '../Spinner'
  import { FormItemReference, formItemProvision, FormItemProvision } from '../Form'
  import { useDebounceWithResponse } from '../../composables'

  import { SwitchContent } from './interface'
  import { MouseState } from '../../states'
  import { SwitchSize, ValidateStatus, ValidateTiming } from '../../enums'

  const props = withDefaults(
    defineProps<{
      modelValue?: boolean
      size?: SwitchSize
      name?: string
      value?: boolean
      checked?: boolean
      autofocus?: boolean
      required?: boolean
      requiredError?: string
      validate?:(args: { modelValue: boolean; fullValue: SwitchContent }) => string | boolean | Promise<string | boolean>
      validateDelay?: number
      validateTiming?: ValidateTiming
      validating?: boolean
      block?: boolean
      extra?: any
      loading?: boolean
      disabled?: boolean
      formItem?: boolean
    }>(),
    {
      modelValue: false,
      size: 'default',
      name: undefined,
      value: true,
      checked: false,
      autofocus: false,
      required: false,
      requiredError: undefined,
      validate: undefined,
      validateDelay: undefined,
      validateTiming: undefined,
      validating: false,
      block: false,
      extra: undefined,
      loading: false,
      disabled: false,
      formItem: true,
    },
  )

  const emits = defineEmits<{(e: 'update:modelValue', modelValue: boolean): void; (e: 'update:validating', validating: boolean): void; (e: 'change', modelValue: boolean, value?: SwitchContent): void; (e: 'update:checked', checked: boolean): void; (e: 'click', event: MouseEvent): void; (e: 'mousedown', event: MouseEvent): void; (e: 'mouseenter', event: MouseEvent): void; (e: 'mouseleave', event: MouseEvent): void; (e: 'mouseup', event: MouseEvent): void; (e: 'focus', event: FocusEvent): void; (e: 'blur', event: FocusEvent): void; (e: 'state-change', state: MouseState): void}>()

  const state = reactive<MouseState>({
    pressing: false,
    hover: false,
  })

  watch(
    state,
    v => emits('state-change', v),
  )

  const formItem = inject<FormItemProvision | undefined>(formItemProvision, undefined)
  const modelValue = ref<boolean>(props.modelValue)

  watch(
    modelValue,
    v => emits('update:modelValue', v),
  )

  watch(
    () => props.modelValue,
    v => {
      modelValue.value = v
    },
  )

  const checked = computed(
    () => modelValue.value === props.value,
  )

  const fullValue = computed(
    () => ({
      ...props,
      modelValue: modelValue.value,
      checked: checked.value,

    }),
  )

  function changeModelValue(v: boolean) {
    if (v !== checked.value) {
      modelValue.value = v ? props.value : !props.value
    }
  }

  watch(
    checked,
    (v, ov) => {
      if (v !== ov) {
        emits('update:checked', v)
        emits('change', v, fullValue.value)
      }
    },
  )

  watch(
    () => props.checked,
    (v, ov) => {
      if (v !== ov) {
        changeModelValue(v)
      }
    },
    { immediate: true },
  )

  const status = ref<ValidateStatus>('default')
  const validateTiming = computed<ValidateTiming>(
    () => props.validateTiming
      || (props.formItem && formItem?.validateTiming.value)
      || 'blur',
  )
  const validating = ref(false)
  const validateError = ref<string>()

  const validate = useDebounceWithResponse(
    () => {
      if (!validating.value) {
        validating.value = true
      }

      const internalValid = (!props.required || checked.value)
        ? true
        : (props.requiredError ?? false)

      if (internalValid === true && props.validate) {
        status.value = 'waiting'

        let tempStatus: ValidateStatus = 'default'
        let tempError: string

        return Promise
          .resolve(
            props.validate({ modelValue: modelValue.value, fullValue: fullValue.value }),
          )
          .then(
            res => {
              if (res !== true) {
                tempStatus = 'error'

                if (typeof res === 'string') {
                  tempError = res
                }
              }

              return res
            },
          )
          .catch(
            () => {
              tempStatus = 'error'

              return '校验异常'
            },
          )
          .finally(
            () => {
              status.value = tempStatus
              validateError.value = tempError
            },
          )
      }

      status.value = internalValid === true ? 'default' : 'error'
      validateError.value = typeof internalValid === 'string' ? internalValid : undefined

      return Promise.resolve(internalValid)
    },
    { delay: props.validateDelay },
  )

  watch(
    validating,
    v => emits('update:validating', v),
  )

  watch(
    () => props.validating,
    v => {
      validating.value = v

      if (v) {
        validate()
      } else {
        status.value = 'default'
      }
    },
    { immediate: true },
  )

  watch(
    [
      () => props.required,
      () => props.validate,
      checked,
    ],
    () => {
      if (validating.value || validateTiming.value === 'immediate') {
        validate()
      }
    },
    { immediate: true },
  )

  const checkbox = ref()
  const focus = ref(false)
  const disabled = computed(
    () => props.loading || props.disabled,
  )

  const cmpClass = computed(
    () => [
      'd-switch',
      'd-clickable',
      `d-switch-${props.size}`,
      props.block
        ? 'd-block'
        : 'd-inline-block',
      disabled.value && 'disabled',
    ],
  )

  const switchClass = computed(
    () => [
      'd-switch-simulator',
      state.pressing && 'pressing',
      state.hover && 'hover',
      ...checked.value
        ? ['checked', '--color-bg-primary']
        : ['unchecked', '--color-bg-fill'],
      focus.value && 'focus',
      disabled.value && 'disabled',
    ],
  )

  function handleClick(e: MouseEvent) {
    emits('click', e)
    if (!disabled.value) {
      if (validating.value !== true && validateTiming.value === 'blur') {
        validating.value = true
      }

      changeModelValue(!checked.value)
    }
  }

  function handleMousedown(e: MouseEvent) {
    emits('mousedown', e)
    state.pressing = true
  }

  function handleMouseenter(e: MouseEvent) {
    emits('mouseenter', e)
    state.hover = true
  }

  function handleMouseleave(e: MouseEvent) {
    emits('mouseleave', e)
    state.pressing = false
    state.hover = false
  }

  function handleMouseup(e: MouseEvent) {
    emits('mouseup', e)
    state.pressing = false
  }

  function handleFocus(e: FocusEvent) {
    emits('focus', e)
    focus.value = true
  }

  function handleBlur(e: FocusEvent) {
    emits('blur', e)
    focus.value = false
  }

  if (props.autofocus) {
    onMounted(
      () => checkbox.value.focus(),
    )
  }

  const $el = ref<HTMLElement>()

  const exposed = reactive<FormItemReference>({
    $el: $el.value,
    modelValue: modelValue.value,
    fullValue: fullValue.value,
    required: props.required,
    validate,
    reset() {
      modelValue.value = undefined
      validating.value = false
      status.value = 'default'
    },
    status: status.value,
    validateError: validateError.value,
    update(v: boolean) {
      modelValue.value = v
    },
  })

  watchEffect(
    () => {
      exposed.$el = $el.value
      exposed.modelValue = modelValue.value
      exposed.fullValue = fullValue.value
      exposed.required = props.required
      exposed.status = status.value
      exposed.validateError = validateError.value
    },
  )

  watch(
    () => props.formItem,
    v => {
      if (v) {
        if (modelValue.value === true) {
          modelValue.value = formItem?.modelValue.value
        }

        formItem?.register(exposed)
      } else {
        formItem?.unregister(exposed)
      }
    },
    { immediate: true },
  )

  onBeforeUnmount(
    () => {
      if (props.formItem) {
        formItem?.unregister(exposed)
      }
    },
  )

  defineExpose(exposed)
</script>
